import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useState, useEffect } from "react";
import {
  fetchProduct,
  fetchProductOptionItem,
  fetchStoreInfo,
} from "./webService/webService";

/**
 * Constants for calculations
 */
const EARTH_RADIUS_KM = 6371.071;
const BASE_DELIVERY_FEE = 100;

/**
 * Calculate the total price of cart items excluding options
 * @param {Array} cartItems - Array of cart items with id and quantity
 * @returns {Promise<number>} Total price excluding options
 */
export async function calculateItemsTotal(cartItems) {
  if (!Array.isArray(cartItems)) return 0;
  
  try {
    const total = await Promise.all(
      cartItems.map(async (item) => {
        const product = await fetchProduct(item.id);
        return product.price * item.quantity;
      })
    );
    
    return total.reduce((sum, amount) => sum + amount, 0);
  } catch (error) {
    console.error('Error calculating items total:', error);
    return 0;
  }
}

/**
 * Calculate the total price of selected options for cart items
 * @param {Array} cartItems - Array of cart items with selected options
 * @returns {Promise<number>} Total price of selected options
 */
export async function calculateOptionsTotal(cartItems) {
  if (!Array.isArray(cartItems)) return 0;

  try {
    const optionsTotals = await Promise.all(
      cartItems.map(async (item) => {
        if (!item.selectedOptions?.length) return 0;

        const optionTotals = await Promise.all(
          item.selectedOptions.map(async (option) => {
            if (!option.optionId || !option.optionItemId) return 0;
            
            const optionData = await fetchProductOptionItem(
              item.id,
              option.optionId,
              option.optionItemId
            );
            return optionData.amount;
          })
        );

        // Option prices are per unit — the same (price + options) × quantity
        // the cart and checkout line items display.
        const perUnit = optionTotals.reduce((sum, amount) => sum + amount, 0);
        return perUnit * item.quantity;
      })
    );

    return optionsTotals.reduce((sum, amount) => sum + amount, 0);
  } catch (error) {
    console.error('Error calculating options total:', error);
    return 0;
  }
}

/**
 * Fetch store location information
 * @param {string} productId - Product ID
 * @returns {Promise<{latitude: number, longitude: number}>} Store location coordinates
 */
export async function fetchStoreLocation(productId) {
  try {
    const storeId = localStorage.getItem("active-store");
    if (!storeId) throw new Error('No active store found');

    const store = await fetchStoreInfo(storeId);
    return {
      latitude: store.location.latitude,
      longitude: store.location.longitude
    };
  } catch (error) {
    console.error('Error fetching store location:', error);
    return { latitude: 0, longitude: 0 };
  }
}

/**
 * Calculate delivery fee based on distance
 * @param {{lat: number, lng: number}} customerLocation - Customer's location
 * @param {string} productId - Product ID
 * @returns {Promise<number>} Delivery fee
 */
export async function calculateDeliveryFee(customerLocation, productId) {
  try {
    const storeLocation = await fetchStoreLocation(productId);
    const distance = calculateHaversineDistance(
      { latitude: storeLocation.latitude, longitude: storeLocation.longitude },
      { latitude: customerLocation.lat, longitude: customerLocation.lng }
    );

    // Simple fee calculation based on distance
    // You can adjust this formula based on your business requirements
    return Math.max(BASE_DELIVERY_FEE, Math.round(distance * 50));
  } catch (error) {
    console.error('Error calculating delivery fee:', error);
    return BASE_DELIVERY_FEE;
  }
}

/**
 * Calculate distance between two points using Haversine formula
 * @param {{latitude: number, longitude: number}} point1 - First point coordinates
 * @param {{latitude: number, longitude: number}} point2 - Second point coordinates
 * @returns {number} Distance in kilometers
 */
export function calculateHaversineDistance(point1, point2) {
  const lat1 = point1.latitude * (Math.PI / 180);
  const lat2 = point2.latitude * (Math.PI / 180);
  const latDiff = lat2 - lat1;
  const lonDiff = (point2.longitude - point1.longitude) * (Math.PI / 180);

  const a = Math.sin(latDiff / 2) * Math.sin(latDiff / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(lonDiff / 2) * Math.sin(lonDiff / 2);

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
}

/**
 * Calculate tax amount
 * @returns {number} Tax amount
 */
export function calculateTax() {
  return 0; // Implement your tax calculation logic here
}

/**
 * Calculate subtotal of cart items including options
 * @param {Array} cartItems - Array of cart items
 * @returns {Promise<number>} Subtotal amount
 */
export async function calculateSubtotal(cartItems) {
  const itemsTotal = await calculateItemsTotal(cartItems);
  const optionsTotal = await calculateOptionsTotal(cartItems);
  return itemsTotal + optionsTotal;
}

/**
 * Calculate total amount including tax and delivery fee
 * @param {Array} cartItems - Array of cart items
 * @param {{lat: number, lng: number}} customerLocation - Customer's location
 * @returns {Promise<number>} Total amount
 */
export async function calculateTotal(cartItems, customerLocation) {
  const subtotal = await calculateSubtotal(cartItems);
  const tax = calculateTax();
  const deliveryFee = await calculateDeliveryFee(customerLocation, cartItems[0]?.id);
  
  return subtotal + tax + deliveryFee;
}
